import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  Observable,
  Subject,
  Subscription,
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  of,
  switchMap,
} from 'rxjs';
import {
  CommissionPartnersGQL,
  SearchDriversForOrderFilterGQL,
  SearchVehiclesForTelematicsFilterGQL,
} from '../../../../generated/graphql';
import { enumLabel } from './driver-closing-ui.util';
import { ReportDropdownCoordinatorService } from './report-dropdown-coordinator.service';

export type ReportEntityMode = 'driver' | 'vehicle' | 'commissionPartner';

export interface ReportEntityOption {
  id: string;
  label: string;
  meta: string;
  mobileNumber?: string;
  plate?: string;
  rate?: string;
  title: string;
  disabled?: boolean;
}

interface SearchTrigger {
  query: string;
  retryToken: number;
}

interface SearchResult {
  options: ReportEntityOption[];
  error: string | null;
}

let nextAutocompleteId = 0;

@Component({
  standalone: false,
  selector: 'app-report-entity-autocomplete',
  templateUrl: './report-entity-autocomplete.component.html',
  styleUrls: ['./report-entity-autocomplete.component.css'],
})
export class ReportEntityAutocompleteComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('searchInput') searchInputElement?: ElementRef<HTMLInputElement>;

  @Input() mode: ReportEntityMode = 'driver';
  @Input() selected: ReportEntityOption | null = null;
  @Input() localOptions: ReportEntityOption[] | null = null;
  @Input() disabled = false;
  @Input() placeholder = 'Search';
  @Input() ariaLabel = 'Search';
  @Output() readonly selectedChange = new EventEmitter<ReportEntityOption | null>();

  searchText = '';
  options: ReportEntityOption[] = [];
  loading = false;
  attempted = false;
  error: string | null = null;
  open = false;
  activeIndex = -1;

  private readonly instanceId = ++nextAutocompleteId;
  @Input() controlKey = `report-entity-${this.instanceId}`;
  readonly panelId = `report-entity-options-${this.instanceId}`;
  private readonly searchRequests = new Subject<SearchTrigger>();
  private readonly subscriptions = new Subscription();
  private blurTimer?: number;
  private retryToken = 0;
  private preserveSearchTextOnNextSelectionChange = false;
  private documentListenersAttached = false;

  private readonly documentPointerDown = (event: PointerEvent): void => {
    if (!this.open || this.eventIsInsideControl(event)) return;
    this.close(false);
  };

  private readonly documentKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || !this.open) return;
    event.preventDefault();
    event.stopPropagation();
    this.close(true);
  };

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly searchDriversGql: SearchDriversForOrderFilterGQL,
    private readonly searchVehiclesGql: SearchVehiclesForTelematicsFilterGQL,
    private readonly commissionPartnersGql: CommissionPartnersGQL,
    private readonly coordinator: ReportDropdownCoordinatorService,
  ) {}

  ngOnInit(): void {
    this.searchText = this.selected?.label ?? '';
    this.bindSearch();
    this.subscriptions.add(
      this.coordinator.activeControl$.subscribe((activeControl) => {
        const shouldOpen = activeControl === this.controlKey;
        if (shouldOpen === this.open) return;
        this.open = shouldOpen;
        if (shouldOpen) {
          this.attachDocumentListeners();
        } else {
          this.detachDocumentListeners();
          this.activeIndex = -1;
        }
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selected'] && !changes['selected'].firstChange) {
      if (this.preserveSearchTextOnNextSelectionChange) {
        this.preserveSearchTextOnNextSelectionChange = false;
        return;
      }
      this.searchText = this.selected?.label ?? '';
      this.resetResults();
    }
    if (changes['localOptions'] && !changes['localOptions'].firstChange) {
      this.applyLocalFilter();
    }
  }

  ngOnDestroy(): void {
    this.cancelBlur();
    this.coordinator.close(this.controlKey);
    this.detachDocumentListeners();
    this.subscriptions.unsubscribe();
  }

  onInput(event: Event): void {
    if (this.disabled) return;
    const value = (event.target as HTMLInputElement).value;
    this.searchText = value;
    this.openControl();

    if (this.selected && value.trim() !== this.selected.label) {
      this.preserveSearchTextOnNextSelectionChange = true;
      this.selectedChange.emit(null);
    }

    if (this.localOptions !== null) {
      this.applyLocalFilter();
      return;
    }

    const query = this.normalizedQuery;
    if (query.length < 2) {
      this.resetResults();
    }
    this.searchRequests.next({ query, retryToken: 0 });
  }

  onFocus(): void {
    if (this.disabled) return;
    this.cancelBlur();
    this.openControl();
    if (
      this.localOptions !== null &&
      this.selected &&
      this.normalizedQuery === this.selected.label.trim()
    ) {
      this.options = [...this.localOptions];
      this.loading = false;
      this.attempted = true;
      this.error = null;
      this.activeIndex = this.firstEnabledIndex(this.options);
      return;
    }
    this.applyLocalFilter();
  }

  onBlur(): void {
    this.cancelBlur();
    this.blurTimer = window.setTimeout(() => this.close(false), 150);
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.disabled) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close(true);
      return;
    }

    if (!this.hasPanel || this.options.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.moveActiveIndex(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.moveActiveIndex(-1);
    } else if (event.key === 'Enter' && this.activeIndex >= 0) {
      event.preventDefault();
      this.select(this.options[this.activeIndex]);
    }
  }

  select(option: ReportEntityOption): void {
    if (this.disabled || option.disabled) return;
    this.cancelBlur();
    this.searchText = option.label;
    this.selectedChange.emit(option);
    this.resetResults();
    this.close(true);
  }

  clear(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.disabled) return;
    this.cancelBlur();
    this.searchText = '';
    this.selectedChange.emit(null);
    this.resetResults();
    this.close(true);
  }

  retry(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.disabled || this.localOptions !== null) return;
    const query = this.normalizedQuery;
    if (query.length < 2) return;
    this.openControl();
    this.searchRequests.next({ query, retryToken: ++this.retryToken });
  }

  optionId(option: ReportEntityOption): string {
    return `${this.panelId}-option-${option.id.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  }

  initials(option: ReportEntityOption): string {
    const words = option.label.trim().split(/\s+/).filter(Boolean);
    return words.slice(0, 2).map((word) => word.charAt(0).toUpperCase()).join('') || 'D';
  }

  trackById(_index: number, option: ReportEntityOption): string {
    return option.id;
  }

  activateOption(index: number, option: ReportEntityOption): void {
    if (!option.disabled) this.activeIndex = index;
  }

  get hasPanel(): boolean {
    if (this.disabled) return false;
    if (this.localOptions !== null) return this.open;
    return this.open && this.normalizedQuery.length >= 2 && (this.loading || this.attempted || !!this.error);
  }

  get emptyMessage(): string {
    if (this.mode === 'driver') return 'No Drivers match this search.';
    if (this.mode === 'vehicle') return 'No vehicles match this search.';
    return 'No commission partners match this search.';
  }

  get activeDescendant(): string | null {
    const option = this.options[this.activeIndex];
    return option ? this.optionId(option) : null;
  }

  private bindSearch(): void {
    this.subscriptions.add(
      this.searchRequests.pipe(
        debounceTime(350),
        distinctUntilChanged((previous, current) =>
          previous.query === current.query && previous.retryToken === current.retryToken,
        ),
        switchMap(({ query }) => {
          if (query.length < 2) {
            return of({ options: [], error: null } as SearchResult);
          }
          this.loading = true;
          this.attempted = true;
          this.error = null;
          this.activeIndex = -1;
          return this.search(query).pipe(
            catchError(() => of({
              options: [],
              error: this.mode === 'driver'
                ? 'Driver search is temporarily unavailable.'
                : 'Commission partner search is temporarily unavailable.',
            })),
          );
        }),
      ).subscribe(({ options, error }) => {
        this.loading = false;
        this.options = options;
        this.error = error;
        this.activeIndex = this.firstEnabledIndex(options);
      }),
    );
  }

  private search(query: string): Observable<SearchResult> {
    if (this.mode === 'driver') {
      return this.searchDriversGql.fetch(
        { query, limit: 10 },
        { fetchPolicy: 'network-only' },
      ).pipe(map((result) => ({
        options: result.data.searchDriversForOrderFilter.slice(0, 10).map((driver) => {
          const name = `${driver.firstName ?? ''} ${driver.lastName ?? ''}`.trim();
          const label = name || `Driver #${driver.id}`;
          return {
            id: driver.id,
            label,
            meta: [driver.mobileNumber, driver.carPlate].filter(Boolean).join(' \u00b7 '),
            mobileNumber: driver.mobileNumber,
            plate: driver.carPlate ?? undefined,
            title: [label, driver.mobileNumber, driver.carPlate].filter(Boolean).join(' \u00b7 '),
          };
        }),
        error: null,
      })));
    }

    if (this.mode === 'vehicle') {
      return this.searchVehiclesGql.fetch(
        { query, limit: 10 },
        { fetchPolicy: 'network-only' },
      ).pipe(map((result) => ({
        options: result.data.searchVehiclesForTelematicsFilter.slice(0, 10).map((vehicle) => ({
          id: vehicle.id,
          label: vehicle.plate,
          meta: [vehicle.modelName, vehicle.colorName, vehicle.assignedDriverName]
            .filter(Boolean)
            .join(' \u00b7 '),
          plate: vehicle.plate,
          title: [vehicle.plate, vehicle.modelName, vehicle.colorName, vehicle.assignedDriverName]
            .filter(Boolean)
            .join(' \u00b7 '),
        })),
        error: null,
      })));
    }

    return this.commissionPartnersGql.fetch(
      { paging: { first: 10, after: 0 }, filter: { name: query } },
      { fetchPolicy: 'network-only' },
    ).pipe(map((result) => ({
      options: result.data.commissionPartners.nodes.slice(0, 10).map((partner) => ({
        id: partner.id,
        label: partner.name,
        meta: enumLabel(partner.type),
        rate: this.trimRate(partner.commissionRatePercent),
        title: `${partner.name} \u00b7 ${enumLabel(partner.type)}`,
      })),
      error: null,
    })));
  }

  private trimRate(value: unknown): string {
    return String(value ?? '')
      .replace(/(\.\d*?[1-9])0+$/, '$1')
      .replace(/\.0+$/, '');
  }

  private close(returnFocus: boolean): void {
    this.cancelBlur();
    this.coordinator.close(this.controlKey);
    this.activeIndex = -1;
    if (returnFocus) {
      window.setTimeout(() => this.searchInputElement?.nativeElement.focus());
    }
  }

  private resetResults(): void {
    this.options = [];
    this.loading = false;
    this.attempted = false;
    this.error = null;
    this.activeIndex = -1;
  }

  private cancelBlur(): void {
    if (this.blurTimer === undefined) return;
    window.clearTimeout(this.blurTimer);
    this.blurTimer = undefined;
  }

  private openControl(): void {
    this.coordinator.open(this.controlKey);
  }

  private eventIsInsideControl(event: Event): boolean {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const host = this.host.nativeElement;
    const target = event.target as Node | null;
    return path.includes(host) || !!(target && host.contains(target));
  }

  private attachDocumentListeners(): void {
    if (this.documentListenersAttached) return;
    const documentRef = this.host.nativeElement.ownerDocument;
    documentRef.addEventListener('pointerdown', this.documentPointerDown);
    documentRef.addEventListener('keydown', this.documentKeydown);
    this.documentListenersAttached = true;
  }

  private detachDocumentListeners(): void {
    if (!this.documentListenersAttached) return;
    const documentRef = this.host.nativeElement.ownerDocument;
    documentRef.removeEventListener('pointerdown', this.documentPointerDown);
    documentRef.removeEventListener('keydown', this.documentKeydown);
    this.documentListenersAttached = false;
  }

  private get normalizedQuery(): string {
    return this.searchText.trim().replace(/\s+/g, ' ');
  }

  private applyLocalFilter(): void {
    if (this.localOptions === null) return;
    const query = this.normalizedQuery.toLocaleLowerCase();
    this.options = this.localOptions.filter((option) =>
      !query || `${option.label} ${option.meta} ${option.rate ?? ''}`.toLocaleLowerCase().includes(query),
    );
    this.loading = false;
    this.attempted = true;
    this.error = null;
    this.activeIndex = this.firstEnabledIndex(this.options);
  }

  private moveActiveIndex(step: 1 | -1): void {
    if (!this.options.some((option) => !option.disabled)) return;
    let index = this.activeIndex;
    for (let count = 0; count < this.options.length; count += 1) {
      index = (index + step + this.options.length) % this.options.length;
      if (!this.options[index].disabled) {
        this.activeIndex = index;
        return;
      }
    }
  }

  private firstEnabledIndex(options: ReportEntityOption[]): number {
    return options.findIndex((option) => !option.disabled);
  }
}
